import React, { useMemo, useState } from "react";
import { apiPost } from "../api";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { BtnSpinner } from "../components/BtnSpinner";

function getPasswordStrengthScore(pw: string): number {
  const p = pw ?? "";
  if (p.length === 0) return 0;

  const hasLower = /[a-z]/.test(p);
  const hasUpper = /[A-Z]/.test(p);
  const hasDigit = /[0-9]/.test(p);
  const hasSymbol = /[^a-zA-Z0-9]/.test(p);
  const variety = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;

  let score = 0;
  if (p.length >= 8) score += 1;
  if (p.length >= 12) score += 1;
  if (variety >= 3) score += 1;
  if (p.length >= 16 && variety >= 3) score += 1;

  if (score < 0) score = 0;
  if (score > 4) score = 4;
  return score;
}

function strengthLabelKey(score: number): string {
  if (score <= 1) return "signup.pw.weak";
  if (score === 2) return "signup.pw.ok";
  if (score === 3) return "signup.pw.good";
  return "signup.pw.strong";
}

export function ResetPasswordView(props: { locale: Locale }) {
  const token = useMemo(() => {
    const u = new URL(window.location.href);
    return u.searchParams.get("token") ?? "";
  }, []);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pwScore = useMemo(() => getPasswordStrengthScore(password), [password]);
  const pwPct = useMemo(() => String(Math.round((pwScore / 4) * 100)), [pwScore]);

  const canSubmit = useMemo(() => {
    return password.length >= 8 && password === password2 && token.length > 0;
  }, [password, password2, token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await apiPost<{ ok: true }>("/v1/password-reset/confirm", { token, password });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  const isInvalidToken = !token;

  return (
    <div className="authShell">
      <div className="authBg" />
      <div className="authWrap">
        <a className="authBrand" href="/" aria-label="KVEJA home">
          <img className="authLogo" src="/brand/kveja_logo.png" alt="KVEJA" />
        </a>

        <div className="authCard">
          <div className="authHeader">
            <h1 className="authTitle">{t(props.locale, "resetPassword.title")}</h1>
            <p className="authSubtitle">{t(props.locale, "resetPassword.subtitle")}</p>
          </div>

          {isInvalidToken ? (
            <div className="authForm">
              <div className="authError">{t(props.locale, "resetPassword.invalidToken")}</div>
              <div className="authRow authRowBottom">
                <a className="btn authBtnSecondary" href="/forgot-password">{t(props.locale, "resetPassword.requestNew")}</a>
              </div>
            </div>
          ) : done ? (
            <div className="authForm">
              <p className="authSuccess">{t(props.locale, "resetPassword.success")}</p>
              <div className="authRow authRowBottom">
                <a className="btn primary authBtn" href="/login">{t(props.locale, "resetPassword.goToLogin")}</a>
              </div>
            </div>
          ) : (
            <form className="authForm" onSubmit={onSubmit}>
              <div className="authField">
                <div className="authLabel">{t(props.locale, "resetPassword.password")}</div>
                <input
                  className="authInput"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder="Minimum 8 characters"
                  required
                />
              </div>

              <div className="pwStrengthRow" aria-live="polite">
                <div className="pwStrengthTop">
                  <div className="small">{t(props.locale, "signup.passwordStrength")}</div>
                  <div className="small"><span className="pwStrengthLabel">{t(props.locale, strengthLabelKey(pwScore))}</span></div>
                </div>
                <div className="pwMeter" role="meter" aria-valuemin={0} aria-valuemax={4} aria-valuenow={pwScore}>
                  <div className="pwMeterFill" style={{ width: `${pwPct}%` }} />
                </div>
              </div>

              <div className="authField">
                <div className="authLabel">{t(props.locale, "resetPassword.password2")}</div>
                <input
                  className="authInput"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder="Repeat password"
                  required
                />
              </div>

              {error ? <div className="authError">{error}</div> : null}

              <div className="authRow authRowBottom">
                <button className="btn primary authBtn" disabled={!canSubmit || busy} type="submit">
                  <BtnSpinner loading={busy}>{busy ? t(props.locale, "resetPassword.submitting") : t(props.locale, "resetPassword.submit")}</BtnSpinner>
                </button>
              </div>

              <div className="authFooter">
                <a className="small" href="/login">{t(props.locale, "forgotPassword.backToLogin")}</a>
              </div>
            </form>
          )}
        </div>

        <div className="authFooter">
          <span className="small">KVEJA</span>
          <span className="authDot" aria-hidden="true">·</span>
          <a className="small" href="/">Back to landing</a>
        </div>
      </div>
    </div>
  );
}
